// Practice: full-screen live view, everything spoken, one observation per
// attempt. The phone faces away from the rider, so the screen is for anyone
// watching (or a front-camera setup) and for reviewing between sets.

import { useEffect, useMemo, useReducer, useRef, useState, useSyncExternalStore } from 'react';
import { PracticeController, type PracticeState } from '../../engine/practice';
import { wakeLockSupported } from '../../engine/wakelock';
import type { FrameSource } from '../../engine/source';
import { rng } from '../../sim/popup';
import { scenarioFor } from '../../sim/live';
import { acquireSource, navigate, refreshModel, useApp, useRoute } from '../app-state';
import { BoardView } from '../components/BoardView';
import { Notice, OutcomeBadge } from '../components/bits';
import { Icon } from '../components/Icon';
import { Stage } from '../components/Stage';
import { drawOverlay } from '../draw/overlay';
import { useLiveFrames } from '../hooks';

export function Practice() {
  const app = useApp();
  const route = useRoute();
  const mode = route.query.get('mode') === 'experiment' ? 'experiment' : 'practice';
  const cal = app.active;
  const [source, setSource] = useState<FrameSource | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [controller, setController] = useState<PracticeController | null>(null);

  useEffect(() => {
    if (!cal) return;
    let cancelled = false;
    acquireSource(cal.source, app.settings, cal.stance)
      .then(async (s) => {
        await refreshModel(s, app.settings);
        if (!cancelled) setSource(s);
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      cancelled = true;
    };
  }, [cal?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Leaving mid-set still saves what was done (or drops an empty session).
  useEffect(
    () => () => {
      if (!controller) return;
      if (controller.getState().phase !== 'done') void controller.finish().finally(() => controller.destroy());
      else controller.destroy();
    },
    [controller],
  );

  // Back from the background: iOS may have ended the camera track.
  useEffect(() => {
    if (!controller || !cal) return;
    const onVisible = async () => {
      if (document.visibilityState !== 'visible') return;
      const src = controller.currentSource;
      if (src.alive()) return;
      try {
        const fresh = await acquireSource(cal.source, app.settings, cal.stance);
        controller.setSource(fresh);
        setSource(fresh);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [controller, cal, app.settings]);

  if (!cal) {
    return (
      <div className="page stack lg">
        <h1>Set up first</h1>
        <p className="muted">Practice needs a camera setup and your target stance.</p>
        <button className="btn primary" onClick={() => navigate('setup')}>Start setup</button>
      </div>
    );
  }

  const start = async () => {
    if (!source) return;
    const c = new PracticeController({ source, calibration: cal, settings: app.settings, mode });
    if (source.sim) wireDemoRider(c, source, app.settings.cueMode);
    setController(c);
    await c.begin();
  };

  return (
    <div className="fullscreen">
      {error && (
        <div className="page stack">
          <Notice kind="error">{error}</Notice>
          <button className="btn" onClick={() => navigate('', { replace: true })}>Back</button>
        </div>
      )}
      {!error && !source && (
        <div className="page row" style={{ justifyContent: 'center', paddingTop: 80 }}>
          <div className="spinner" /> <span>Starting {cal.source === 'demo' ? 'the demo' : 'the camera'}…</span>
        </div>
      )}
      {source && !controller && <Ready source={source} mode={mode} onStart={start} />}
      {source && controller && <Running source={source} controller={controller} mode={mode} onSourceChange={setSource} />}
    </div>
  );
}

/** In demo mode the virtual rider follows the loop's spoken cues. */
function wireDemoRider(c: PracticeController, source: FrameSource, cueMode: string) {
  const sim = source.sim!;
  const r = rng(Date.now() % 100000);
  let lieTimer: ReturnType<typeof setTimeout> | null = null;
  c.onCue = (cue) => {
    if (cue === 'getReady') {
      if (lieTimer) clearTimeout(lieTimer);
      lieTimer = setTimeout(() => sim.lieDown(performance.now(), cueMode === 'none'), 1200 + r.next() * 800);
    } else if (cue === 'go') {
      const sc = c.getState().scenario;
      setTimeout(() => sim.popUp(performance.now(), sc ? scenarioFor(sc.key, r) : undefined), 0);
    }
  };
}

function Ready({ source, mode, onStart }: { source: FrameSource; mode: string; onStart: () => void }) {
  const app = useApp();
  const cal = app.active!;
  const { latest, sample } = useLiveFrames(source);
  void sample; // re-render as frames arrive so the orientation check stays current
  const aspect = source.width() / Math.max(1, source.height());
  const mismatch = source.width() > 0 && Math.abs(aspect - cal.mapping.aspect) / cal.mapping.aspect > 0.05;
  useEffect(() => {
    source.sim?.standInStance(performance.now());
  }, [source]);
  const cueText = { auto: 'Beep once you’re lying down', timer: 'Beep on a timer', none: 'Go whenever you like' }[app.settings.cueMode];
  return (
    <>
      <Stage
        view={source.view}
        size={() => ({ w: source.width(), h: source.height() })}
        draw={(ctx, r, dpr) =>
          drawOverlay(
            ctx,
            r,
            {
              mapping: cal.mapping,
              tape: cal.tape,
              reference: cal.reference,
              tol: tolOf(app.settings.strictness),
              lm: latest.current?.lm,
              stance: cal.stance,
              skeleton: app.settings.showSkeleton,
            },
            dpr,
          )
        }
      />
      <div className="hud top">
        <button className="icon-btn" onClick={() => navigate('', { replace: true })} aria-label="Close">
          <Icon name="x" />
        </button>
        <div className="grow" />
        {mode === 'experiment' && <span className="pill"><Icon name="flask" size={15} /> Validation set</span>}
        {cal.source === 'demo' && <span className="pill">Demo</span>}
      </div>
      <div className="hud bottom">
        <div className="glass" style={{ padding: 18, maxWidth: 520, margin: '0 auto' }}>
          <div className="stack">
            <div>
              <h2>{mode === 'experiment' ? 'Validation set' : 'Ready when you are'}</h2>
              <p className="small" style={{ color: 'rgba(238,243,246,0.75)', marginTop: 4 }}>
                {mode === 'experiment'
                  ? 'Before each attempt you’ll hear what to do — some on purpose wrong. Results are pre-labelled so you can check the app’s accuracy.'
                  : `${app.settings.attemptsPerSet ? `${app.settings.attemptsPerSet} attempts` : 'Open-ended set'} · ${cueText} · hold each landing for ${(app.settings.holdMs / 1000).toFixed(1)} s`}
              </p>
            </div>
            <ul className="small" style={{ margin: 0, paddingLeft: 18, color: 'rgba(238,243,246,0.75)', display: 'grid', gap: 4 }}>
              <li>Turn the volume up — everything is spoken.</li>
              <li>Pop up after the beep, land, and hold still until the chime.</li>
              {!wakeLockSupported() && <li>If the screen dims mid-set, set Auto-Lock to Never (Settings → Display &amp; Brightness).</li>}
            </ul>
            {mismatch ? (
              <Notice kind="warn">
                The camera picture is {source.height() > source.width() ? 'portrait' : 'a different shape'} but this setup was calibrated
                {cal.mapping.aspect > 1 ? ' in landscape' : ''}. Turn the phone back the way it was — and turn off Portrait Orientation Lock — or recalibrate.
              </Notice>
            ) : (
              <button className="btn primary hero block" onClick={onStart}>
                <Icon name="play" /> Start set
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

const tolOf = (s: string) => ({ relaxed: { along: 12, across: 12 }, standard: { along: 8, across: 8 }, strict: { along: 5, across: 5 } })[s] ?? { along: 8, across: 8 };

function Running({
  source,
  controller,
  mode,
  onSourceChange,
}: {
  source: FrameSource;
  controller: PracticeController;
  mode: string;
  onSourceChange: (s: FrameSource) => void;
}) {
  const app = useApp();
  const cal = app.active!;
  const state = useSyncExternalStore(controller.subscribe, controller.getState);
  const stateRef = useRef<PracticeState>(state);
  stateRef.current = state;
  const params = controller.analysisParams;
  // Refresh the frame-rate readout once a second (the overlay itself redraws every frame).
  const [, tick] = useReducer((x: number) => x + 1, 0);
  useEffect(() => {
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  const tol = useMemo(() => ({ along: params.tolAlong, across: params.tolAcross }), [params]);

  useEffect(() => {
    if (state.phase === 'done' && state.sessionId) {
      const t = setTimeout(() => navigate(`session/${state.sessionId}`, { replace: true }), 1800);
      return () => clearTimeout(t);
    }
  }, [state.phase, state.sessionId]);

  const end = async () => {
    const id = await controller.finish();
    navigate(id ? `session/${id}` : '', { replace: true });
  };

  // Resuming after the camera stopped: re-open it first if the track ended.
  const resume = async () => {
    const src = controller.currentSource;
    if (!src.alive()) {
      try {
        const fresh = await acquireSource(cal.source, app.settings, cal.stance);
        controller.setSource(fresh);
        onSourceChange(fresh);
      } catch {
        return;
      }
    }
    controller.resume();
  };

  const last = state.lastAttempt;
  const showResult = state.phase === 'review' && state.lastFeedback;
  const outcomeClass = showResult && last ? last.analysis.outcome : '';
  const stats = source.stats();

  return (
    <>
      <Stage
        view={source.view}
        size={() => ({ w: source.width(), h: source.height() })}
        draw={(ctx, r, dpr) => {
          const s = stateRef.current;
          drawOverlay(
            ctx,
            r,
            {
              mapping: cal.mapping,
              tape: cal.tape,
              reference: cal.reference,
              tol,
              lm: controller.latest?.lm,
              stance: cal.stance,
              skeleton: app.settings.showSkeleton,
              quietTargets: s.phase === 'getReady' || s.phase === 'armed',
            },
            dpr,
          );
        }}
      />
      <div className="hud top">
        <button className="icon-btn" onClick={end} aria-label="Close">
          <Icon name="x" />
        </button>
        <span className="pill num" aria-label="Attempts">
          {state.count}
          {state.target ? ` / ${state.target}` : ''}
        </span>
        {mode === 'experiment' && <span className="pill"><Icon name="flask" size={15} /> Validation</span>}
        <div className="grow" />
        {!state.personVisible && state.phase !== 'paused' && <span className="pill">Can’t see you</span>}
        {state.slow && (
          <span className="pill" title="Below 20 analysed frames per second, brief foot adjustments can be missed. Try the Lite model in Settings.">
            Slow analysis
          </span>
        )}
        <span className="pill num" title="Analysed frames per second">{stats.fps ? `${stats.fps.toFixed(0)} fps` : '…'}</span>
      </div>
      <div className="hud bottom">
        <div className="row" style={{ alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
          <div className="grow" aria-live="polite">
            <div className={`headline ${outcomeClass}`}>{showResult ? state.lastFeedback!.headline : state.headline}</div>
            {(showResult ? state.lastFeedback!.spoken : state.detail) && <div className="subline">{showResult ? state.lastFeedback!.spoken : state.detail}</div>}
          </div>
          {last && (
            <div className="glass result-card">
              <div className="row between" style={{ marginBottom: 6 }}>
                <span className="small num" style={{ opacity: 0.7 }}>#{last.index + 1}</span>
                <OutcomeBadge outcome={last.analysis.outcome} />
              </div>
              <BoardView
                compact
                reference={cal.reference}
                stance={cal.stance}
                tol={tol}
                initial={last.analysis.initial}
                settled={last.analysis.settled}
                adjustments={last.analysis.adjustments}
              />
            </div>
          )}
        </div>
        {state.phase === 'cameraMoved' && (
          <div className="glass stack" style={{ padding: 14, marginTop: 12 }}>
            <p>{state.detail}</p>
            <div className="row">
              <button className="btn primary" onClick={() => navigate('setup?recal=1')}>Recalibrate</button>
              <button className="btn" onClick={() => controller.resume()}>Continue anyway</button>
            </div>
          </div>
        )}
        <div className="row" style={{ marginTop: 14 }}>
          {state.phase === 'paused' ? (
            <button className="btn primary" onClick={() => void resume()}>
              <Icon name="play" /> Resume
            </button>
          ) : (
            <button className="btn" onClick={() => controller.pause()} disabled={state.phase === 'done' || state.phase === 'cameraMoved'}>
              <Icon name="pause" /> Pause
            </button>
          )}
          <button className="btn" onClick={end}>
            <Icon name="stop" /> End set
          </button>
        </div>
      </div>
    </>
  );
}
